import type { EntityId } from '../api/entityId';

export interface DocumentDraftRecord {
  userId: EntityId;
  documentId: EntityId;
  contentMarkdown: string;
  baseContentVersion: number;
  updatedAt: number;
}

interface StoredDocumentDraftRecord extends DocumentDraftRecord {
  key: string;
}

const databaseName = 'knowledgebase.documentDrafts.v1';
const databaseVersion = 1;
const storeName = 'documentDrafts';

let databasePromise: Promise<IDBDatabase> | null = null;

export async function getDocumentDraft(userId: EntityId, documentId: EntityId): Promise<DocumentDraftRecord | null> {
  const database = await openDatabase();
  const record = await requestToPromise<StoredDocumentDraftRecord | undefined>(
    database
      .transaction(storeName, 'readonly')
      .objectStore(storeName)
      .get(getDraftKey(userId, documentId))
  );

  if (record == null) {
    return null;
  }

  if (!isStoredDocumentDraftRecord(record)) {
    await deleteDocumentDraft(userId, documentId);
    return null;
  }

  return {
    userId: record.userId,
    documentId: record.documentId,
    contentMarkdown: record.contentMarkdown,
    baseContentVersion: record.baseContentVersion,
    updatedAt: record.updatedAt
  };
}

export async function setDocumentDraft(record: DocumentDraftRecord): Promise<void> {
  const database = await openDatabase();
  await requestToPromise(
    database
      .transaction(storeName, 'readwrite')
      .objectStore(storeName)
      .put({ ...record, key: getDraftKey(record.userId, record.documentId) })
  );
}

export async function deleteDocumentDraft(userId: EntityId, documentId: EntityId): Promise<void> {
  const database = await openDatabase();
  await requestToPromise(
    database
      .transaction(storeName, 'readwrite')
      .objectStore(storeName)
      .delete(getDraftKey(userId, documentId))
  );
}

function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) {
    return databasePromise;
  }

  const promise = new Promise<IDBDatabase>((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('Document draft storage is not available in this browser.'));
      return;
    }

    const request = window.indexedDB.open(databaseName, databaseVersion);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(storeName)) {
        database.createObjectStore(storeName, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Unable to open document draft storage.'));
    request.onblocked = () => reject(new Error('Document draft storage is blocked by another browser tab.'));
  }).catch((error: unknown): never => {
    databasePromise = null;
    throw error;
  });

  databasePromise = promise;
  return promise;
}

function requestToPromise<TResult>(request: IDBRequest<TResult>): Promise<TResult> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Document draft storage request failed.'));
  });
}

function getDraftKey(userId: EntityId, documentId: EntityId): string {
  return `${userId}:${documentId}`;
}

function isStoredDocumentDraftRecord(value: unknown): value is StoredDocumentDraftRecord {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.key === 'string'
    && typeof candidate.userId === 'string'
    && typeof candidate.documentId === 'string'
    && typeof candidate.contentMarkdown === 'string'
    && typeof candidate.baseContentVersion === 'number'
    && Number.isInteger(candidate.baseContentVersion)
    && candidate.baseContentVersion >= 0
    && typeof candidate.updatedAt === 'number'
    && Number.isFinite(candidate.updatedAt)
    && candidate.updatedAt >= 0;
}
